/**
 * Fall pace: how long a block takes to fall the full well.
 *
 *   fallTimeMs = clamp(parTimeMs × slack(level) × runPace × levelPace × ratingPace, MIN_FALL_MS, MAX_FALL_MS)
 *
 * Scaling by the problem's own par time keeps a 20-second simultaneous-equation
 * problem as fair as a 6-second addition. Spec: docs/GAME_DESIGN.md § 4.
 */
import { PACE } from '../config.ts';
import { clamp } from '../math/stats.ts';
import type { LevelDef, Problem } from '../types.ts';
import { bandFraction } from './rating.ts';
import type { PaceConstants } from './types.ts';

/** Run-long Tetris-style ramp by answer count: 1 → RUN_FLOOR. Every run eventually ends. */
export function runPace(runAnswered: number, p: PaceConstants = PACE): number {
  return Math.max(p.RUN_FLOOR, 1 / (1 + Math.max(0, runAnswered) / p.RUN_HALF));
}

/** In-level ramp: 1 → LEVEL_FLOOR as the player answers more at the current level. */
export function levelPace(levelAnswered: number, p: PaceConstants = PACE): number {
  return Math.max(p.LEVEL_FLOOR, 1 - p.LEVEL_RAMP * Math.max(0, levelAnswered));
}

/** Slightly faster near the top of the level band: 1 at the band's bottom → 1 − RATING_TWEAK at its top. */
export function ratingPace(rating: number, level: number, p: PaceConstants = PACE): number {
  return 1 - p.RATING_TWEAK * bandFraction(rating, level);
}

/**
 * Full-well fall time for a problem (ms, rounded to a whole millisecond so lock
 * times stay readable in logs). `ctx.runAnswered` / `ctx.levelAnswered` count
 * answers given BEFORE this problem spawns.
 */
export function fallTimeMs(
  problem: Problem,
  level: LevelDef,
  ctx: { runAnswered: number; levelAnswered: number; rating: number },
  p: PaceConstants = PACE,
): number {
  const raw =
    problem.parTimeMs *
    level.slack *
    runPace(ctx.runAnswered, p) *
    levelPace(ctx.levelAnswered, p) *
    ratingPace(ctx.rating, level.index, p);
  return Math.round(clamp(raw, p.MIN_FALL_MS, p.MAX_FALL_MS));
}
